/**
 * The one place vocabulary state lives at runtime.
 *
 * Sources emit FeatureFrames; this turns each into the frame's ControlState by
 * running applyVocabulary over a single held memory. One instance per app —
 * NOT one per scene adapter. When every adapter held its own VocabMemory, the
 * same physical pinch was re-detected in five places with five memories, and
 * whichever adapter a scene consulted decided what the hand was doing.
 *
 * Stateful but DOM-free and audio-free, so a fixture replay drives it exactly
 * as the camera does: replay the same fixture twice, get byte-identical
 * ControlState streams — the Phase 1 exit assertion, literally executable.
 */

import type { ControlState, FeatureFrame } from './types';
import { applyVocabulary, DEFAULT_VOCAB, freshMemory, type VocabConfig, type VocabMemory } from './vocabulary';

export interface ControlPipeline {
  update(frame: FeatureFrame): ControlState;
  /** Forget everything held. Call on scene switch and on input-source switch. */
  reset(): void;
  /**
   * How many zones the height axis quantizes into. Scene-owned: Bass and Arp
   * play 7 scale degrees, Tala selects among 8 strokes, Amen among its
   * slices-per-bar. The held degree is clamped into the new range rather than
   * reset — a hand that has not moved should not change its answer more than
   * the resize forces it to.
   */
  setSelectionSteps(n: number): void;
  readonly selectionSteps: number;
}

export function createControlPipeline(cfg: VocabConfig = DEFAULT_VOCAB): ControlPipeline {
  let mem: VocabMemory = freshMemory();
  // A mutable copy: `degrees` is the one field a scene may vary. Everything
  // else in the vocabulary stays exactly as configured.
  let live: VocabConfig = { ...cfg };
  return {
    update(frame) {
      const { state, memory } = applyVocabulary(frame, mem, live);
      mem = memory;
      return state;
    },
    reset() { mem = freshMemory(); },
    setSelectionSteps(n) {
      const steps = Math.max(1, Math.min(32, Math.round(n)));
      if (steps === live.degrees) return;
      live = { ...live, degrees: steps };
      mem = { ...mem, degree: Math.min(mem.degree, steps - 1) };
    },
    get selectionSteps() { return live.degrees; },
  };
}
